import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _assertThisInitialized from "@babel/runtime/helpers/esm/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import { createElement } from "@wordpress/element";

/**
 * Parts of this source were derived and modified from react-color,
 * released under the MIT license.
 *
 * https://github.com/casesandberg/react-color/
 *
 * Copyright (c) 2015 Case Sandberg
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 * THE SOFTWARE.
 */

/**
 * External dependencies
 */
import { clamp, noop, throttle } from 'lodash';
/**
 * WordPress dependencies
 */

import { __ } from '@wordpress/i18n';
import { Component, createRef } from '@wordpress/element';
import { TAB } from '@wordpress/keycodes';
import { compose, pure, withInstanceId } from '@wordpress/compose';
/**
 * Internal dependencies
 */

import { calculateSaturationChange } from './utils';
import KeyboardShortcuts from '../keyboard-shortcuts';
export var Saturation =
/*#__PURE__*/
function (_Component) {
  _inherits(Saturation, _Component);

  function Saturation(props) {
    var _this;

    _classCallCheck(this, Saturation);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(Saturation).call(this, props));
    _this.throttle = throttle(function (fn, data, e) {
      fn(data, e);
    }, 50);
    _this.container = createRef();
    _this.saturate = _this.saturate.bind(_assertThisInitialized(_this));
    _this.brighten = _this.brighten.bind(_assertThisInitialized(_this));
    _this.handleChange = _this.handleChange.bind(_assertThisInitialized(_this));
    _this.handleMouseDown = _this.handleMouseDown.bind(_assertThisInitialized(_this));
    _this.handleMouseUp = _this.handleMouseUp.bind(_assertThisInitialized(_this));
    return _this;
  }

  _createClass(Saturation, [{
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      this.throttle.cancel();
      this.unbindEventListeners();
    }
  }, {
    key: "saturate",
    value: function saturate() {
      var amount = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0.01;
      var _this$props = this.props,
          hsv = _this$props.hsv,
          _this$props$onChange = _this$props.onChange,
          onChange = _this$props$onChange === void 0 ? noop : _this$props$onChange;
      var intSaturation = clamp(hsv.s + Math.round(amount * 100), 0, 100);
      var change = {
        h: hsv.h,
        s: intSaturation,
        v: hsv.v,
        a: hsv.a,
        source: 'rgb'
      };
      onChange(change);
    }
  }, {
    key: "brighten",
    value: function brighten() {
      var amount = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0.01;
      var _this$props2 = this.props,
          hsv = _this$props2.hsv,
          _this$props2$onChange = _this$props2.onChange,
          onChange = _this$props2$onChange === void 0 ? noop : _this$props2$onChange;
      var intValue = clamp(hsv.v + Math.round(amount * 100), 0, 100);
      var change = {
        h: hsv.h,
        s: hsv.s,
        v: intValue,
        a: hsv.a,
        source: 'rgb'
      };
      onChange(change);
    }
  }, {
    key: "handleChange",
    value: function handleChange(e) {
      var _this$props$onChange2 = this.props.onChange,
          onChange = _this$props$onChange2 === void 0 ? noop : _this$props$onChange2;
      var change = calculateSaturationChange(e, this.props, this.container.current);
      this.throttle(onChange, change, e);
    }
  }, {
    key: "handleMouseDown",
    value: function handleMouseDown(e) {
      this.handleChange(e);
      window.addEventListener('mousemove', this.handleChange);
      window.addEventListener('mouseup', this.handleMouseUp);
    }
  }, {
    key: "handleMouseUp",
    value: function handleMouseUp() {
      this.unbindEventListeners();
    }
  }, {
    key: "preventKeyEvents",
    value: function preventKeyEvents(event) {
      if (event.keyCode === TAB) {
        return;
      }

      event.preventDefault();
    }
  }, {
    key: "unbindEventListeners",
    value: function unbindEventListeners() {
      window.removeEventListener('mousemove', this.handleChange);
      window.removeEventListener('mouseup', this.handleMouseUp);
    }
  }, {
    key: "render",
    value: function render() {
      var _this2 = this;

      var _this$props3 = this.props,
          hsv = _this$props3.hsv,
          hsl = _this$props3.hsl,
          instanceId = _this$props3.instanceId;
      var pointerLocation = {
        top: "".concat(-hsv.v + 100, "%"),
        left: "".concat(hsv.s, "%")
      };
      var shortcuts = {
        up: function up() {
          return _this2.brighten();
        },
        'shift+up': function shiftUp() {
          return _this2.brighten(0.1);
        },
        pageup: function pageup() {
          return _this2.brighten(1);
        },
        down: function down() {
          return _this2.brighten(-0.01);
        },
        'shift+down': function shiftDown() {
          return _this2.brighten(-0.1);
        },
        pagedown: function pagedown() {
          return _this2.brighten(-1);
        },
        right: function right() {
          return _this2.saturate();
        },
        'shift+right': function shiftRight() {
          return _this2.saturate(0.1);
        },
        end: function end() {
          return _this2.saturate(1);
        },
        left: function left() {
          return _this2.saturate(-0.01);
        },
        'shift+left': function shiftLeft() {
          return _this2.saturate(-0.1);
        },
        home: function home() {
          return _this2.saturate(-1);
        }
      };
      /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */

      return createElement(KeyboardShortcuts, {
        shortcuts: shortcuts
      }, createElement("div", {
        style: {
          background: "hsl(".concat(hsl.h, ",100%, 50%)")
        },
        className: "components-color-picker__saturation-color",
        ref: this.container,
        onMouseDown: this.handleMouseDown,
        onTouchMove: this.handleChange,
        onTouchStart: this.handleChange,
        role: "application"
      }, createElement("div", {
        className: "components-color-picker__saturation-white"
      }), createElement("div", {
        className: "components-color-picker__saturation-black"
      }), createElement("button", {
        "aria-label": __('Choose a shade'),
        "aria-describedby": "color-picker-saturation-".concat(instanceId),
        className: "components-color-picker__saturation-pointer",
        style: pointerLocation,
        onKeyDown: this.preventKeyEvents
      }), createElement("div", {
        className: "screen-reader-text",
        id: "color-picker-saturation-".concat(instanceId)
      }, __('Use your arrow keys to change the base color. Move up to lighten the color, down to darken, left to decrease saturation, and right to increase saturation.'))));
      /* eslint-enable jsx-a11y/no-noninteractive-element-interactions */
    }
  }]);

  return Saturation;
}(Component);
export default compose(pure, withInstanceId)(Saturation);
//# sourceMappingURL=saturation.js.map